import { Controller } from "@hotwired/stimulus"

// Reseller chat: persistent conversations backed by the knowledge_server API.
// Every conversation starts with an explicit mode (customer email / codebase /
// feature request) so the answer can be tailored to the question being asked.
export default class extends Controller {
  static targets = ["messages", "input", "send", "list", "empty", "form", "modePicker", "modeBar", "modeLabel"]
  static values = {
    conversationId: Number,
    mode: String,
    modeLabel: String,
    createUrl: String,
    showUrl: String,
    updateUrl: String,
    messagesUrl: String,
    handoffUrl: String,
    thinkingText: String,
    errorText: String,
    emptyText: String,
    modeRequiredText: String
  }

  connect() {
    this.busy = false
    this.applyMode(this.modeValue, this.modeLabelValue)
    this.scrollToBottom()
  }

  async chooseMode(event) {
    event.preventDefault()
    const mode = event.currentTarget.dataset.mode
    if (!mode) return

    const label = event.currentTarget.dataset.label || mode
    this.applyMode(mode, label)

    if (this.conversationIdValue) {
      try {
        await this.request(this.updateUrlValue.replace("CONVERSATION_ID", this.conversationIdValue), {
          method: "PATCH",
          body: { mode }
        })
      } catch (error) {
        console.error("reseller chat mode update failed", error)
        this.appendError(this.errorTextValue)
      }
    }

    this.inputTarget.focus()
  }

  changeMode(event) {
    event.preventDefault()
    this.showPicker()
  }

  async send(event) {
    event.preventDefault()
    if (!this.modeValue) {
      this.showPicker()
      return
    }

    const text = this.inputTarget.value.trim()
    if (!text || this.busy) return

    this.busy = true
    this.setLoading(true)

    try {
      const conversationId = this.conversationIdValue || (await this.createConversation())
      this.clearEmpty()
      this.appendUserMessage(text)
      const thinking = this.appendThinking()

      const response = await this.request(this.messagesUrlValue.replace("CONVERSATION_ID", conversationId), {
        method: "POST",
        body: { message: text }
      })

      thinking.remove()
      this.appendHtml(response.assistant_html)
      this.updateTitle(conversationId, response.title)
      this.inputTarget.value = ""
    } catch (error) {
      console.error("reseller chat failed", error)
      const thinking = this.messagesTarget.querySelector(".c-chat__thinking")
      if (thinking) thinking.remove()
      this.appendError(this.errorTextValue)
    } finally {
      this.busy = false
      this.setLoading(false)
      this.scrollToBottom()
    }
  }

  async handoff(event) {
    event.preventDefault()
    if (this.busy) return

    const messageId = event.currentTarget.dataset.messageId
    const conversationId = this.conversationIdValue
    if (!messageId || !conversationId) return

    const button = event.currentTarget
    this.busy = true
    button.disabled = true

    try {
      const response = await this.request(this.handoffUrlValue.replace("CONVERSATION_ID", conversationId), {
        method: "POST",
        body: { message_id: messageId }
      })

      this.conversationIdValue = Number(response.id)
      this.messagesTarget.innerHTML = (response.user_html || "") + (response.assistant_html || "")
      this.applyMode(response.mode, response.mode_label)
      this.prependConversation(response.id, response.title)
      this.markActive(String(response.id))
      this.scrollToBottom()
    } catch (error) {
      console.error("reseller chat handoff failed", error)
      this.appendError(this.errorTextValue)
    } finally {
      this.busy = false
      button.disabled = false
    }
  }

  async selectConversation(event) {
    event.preventDefault()
    if (this.busy) return

    const id = event.currentTarget.dataset.conversationId
    if (!id) return

    this.busy = true
    this.setLoading(true)

    try {
      const response = await this.request(this.showUrlValue.replace("CONVERSATION_ID", id), { method: "GET" })
      this.messagesTarget.innerHTML = response.messages_html
      this.conversationIdValue = Number(id)
      this.applyMode(response.mode || "", response.mode_label || "")
      this.markActive(id)
      this.scrollToBottom()
    } catch (error) {
      console.error("reseller chat load failed", error)
      this.appendError(this.errorTextValue)
    } finally {
      this.busy = false
      this.setLoading(false)
    }
  }

  newConversation(event) {
    event.preventDefault()
    if (this.busy) return

    this.conversationIdValue = 0
    this.applyMode("", "")
    this.messagesTarget.innerHTML = ""
    this.clearEmpty()
    this.messagesTarget.appendChild(this.buildEmptyState())
    this.markActive(null)
  }

  async createConversation() {
    if (!this.modeValue) {
      throw new Error(this.modeRequiredTextValue || "mode required")
    }

    const response = await this.request(this.createUrlValue, { method: "POST", body: { mode: this.modeValue } })
    this.conversationIdValue = Number(response.id)
    this.prependConversation(response.id, response.title)
    this.markActive(String(response.id))
    return response.id
  }

  // Single place that decides what the UI shows for a given mode, so the
  // picker, the mode bar and the composer can never disagree.
  applyMode(mode, label) {
    this.modeValue = mode || ""
    this.modeLabelValue = label || ""

    if (this.modeValue) {
      this.showConversationChrome(this.modeLabelValue)
    } else {
      this.showPicker()
    }
  }

  showPicker() {
    if (this.hasModePickerTarget) this.modePickerTarget.hidden = false
    if (this.hasModeBarTarget) this.modeBarTarget.hidden = true
    if (this.hasFormTarget) this.formTarget.hidden = true
    this.markSelectedMode(this.modeValue)
  }

  showConversationChrome(label) {
    if (this.hasModePickerTarget) this.modePickerTarget.hidden = true
    if (this.hasModeBarTarget) this.modeBarTarget.hidden = false
    if (this.hasModeLabelTarget) this.modeLabelTarget.textContent = label
    if (this.hasFormTarget) this.formTarget.hidden = false
  }

  markSelectedMode(mode) {
    if (!this.hasModePickerTarget) return
    this.modePickerTarget.querySelectorAll(".c-chat__mode-option").forEach((option) => {
      option.classList.toggle("is-selected", option.dataset.mode === mode)
    })
  }

  async request(url, { method, body }) {
    const options = {
      method,
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken()
      }
    }

    if (body) {
      options.headers["Content-Type"] = "application/json"
      options.body = JSON.stringify(body)
    }

    const response = await fetch(url, options)
    if (!response.ok) {
      throw new Error(`request failed with ${response.status}`)
    }

    return response.json()
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }

  appendUserMessage(text) {
    const message = document.createElement("div")
    message.className = "c-chat__message c-chat__message--user"

    const bubble = document.createElement("div")
    bubble.className = "c-chat__bubble"
    bubble.textContent = text

    message.appendChild(bubble)
    this.messagesTarget.appendChild(message)
    this.scrollToBottom()
  }

  appendThinking() {
    const message = document.createElement("div")
    message.className = "c-chat__message c-chat__message--assistant c-chat__thinking"

    const bubble = document.createElement("div")
    bubble.className = "c-chat__bubble"
    bubble.textContent = this.thinkingTextValue

    message.appendChild(bubble)
    this.messagesTarget.appendChild(message)
    this.scrollToBottom()
    return message
  }

  appendError(text) {
    const message = document.createElement("div")
    message.className = "c-chat__message c-chat__message--assistant"

    const bubble = document.createElement("div")
    bubble.className = "c-chat__bubble c-chat__bubble--error"
    bubble.textContent = text

    message.appendChild(bubble)
    this.messagesTarget.appendChild(message)
    this.scrollToBottom()
  }

  appendHtml(html) {
    if (!html) return
    this.messagesTarget.insertAdjacentHTML("beforeend", html)
  }

  buildEmptyState() {
    const empty = document.createElement("div")
    empty.className = "c-chat__empty"
    empty.dataset.resellerChatTarget = "empty"
    empty.textContent = this.emptyTextValue
    return empty
  }

  clearEmpty() {
    this.messagesTarget.querySelectorAll(".c-chat__empty").forEach((element) => element.remove())
  }

  prependConversation(id, title) {
    if (!this.hasListTarget) return

    const item = document.createElement("li")
    const link = document.createElement("a")
    link.href = "#"
    link.className = "c-chat__conversation"
    link.dataset.action = "reseller-chat#selectConversation"
    link.dataset.conversationId = id
    link.textContent = title

    item.appendChild(link)
    this.listTarget.prepend(item)
  }

  updateTitle(id, title) {
    if (!this.hasListTarget || !title) return
    const link = this.listTarget.querySelector(`[data-conversation-id="${id}"]`)
    if (link) link.textContent = title
  }

  markActive(id) {
    if (!this.hasListTarget) return
    this.listTarget.querySelectorAll(".c-chat__conversation").forEach((link) => {
      link.classList.toggle("is-active", link.dataset.conversationId === String(id))
    })
  }

  setLoading(loading) {
    if (this.hasInputTarget) this.inputTarget.disabled = loading
    if (this.hasSendTarget) this.sendTarget.disabled = loading
  }

  scrollToBottom() {
    if (!this.hasMessagesTarget) return
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }
}
